import React, { useEffect, useRef, useState } from 'react';
import { renderAsync } from 'docx-preview';
import styles from './styles.module.scss';

interface PdfViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfFile?: any;
}

// Returns the file extension (lower-cased, no dot) for a URL or filename,
// ignoring any query string / hash that signed storage URLs often carry.
const getFileExtension = (file: string): string => {
  const clean = file.split(/[?#]/)[0];
  const match = clean.match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : '';
};

// Legacy binary Office formats that docx-preview can't parse — fall back to the
// Office Online viewer for these (only .docx is rendered client-side).
const OFFICE_VIEWER_EXTENSIONS = ['doc', 'ppt', 'pptx', 'xls', 'xlsx'];

const PdfViewerModal: React.FC<PdfViewerModalProps> = ({ isOpen, onClose, pdfFile }) => {
  const docxContainerRef = useRef<HTMLDivElement>(null);
  const [docxStatus, setDocxStatus] = useState<'idle' | 'loading' | 'error' | 'done'>('idle');

  const fileUrl = typeof pdfFile === 'string' ? pdfFile : '';
  const extension = getFileExtension(fileUrl);
  const isDocx = extension === 'docx';
  const isLegacyOffice = OFFICE_VIEWER_EXTENSIONS.includes(extension);

  // A .docx served with its real MIME type only downloads inside an <iframe>, so
  // we fetch it (Cloudinary allows CORS) and render it inline with docx-preview.
  useEffect(() => {
    if (!isOpen || !isDocx || !fileUrl) return;

    let cancelled = false;
    setDocxStatus('loading');

    (async () => {
      try {
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error(`Failed to fetch document (${response.status})`);
        const blob = await response.blob();
        const container = docxContainerRef.current;
        if (cancelled || !container) return;
        container.innerHTML = '';
        await renderAsync(blob, container, undefined, {
          className: 'docx',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
        });
        if (!cancelled) setDocxStatus('done');
      } catch (err) {
        if (!cancelled) setDocxStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, isDocx, fileUrl]);

  if (!isOpen) return null;

  const iframeSrc = isLegacyOffice
    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`
    : fileUrl;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.content}>
          {!fileUrl ? (
            <div className="flex flex-col items-center justify-center w-full h-full gap-3 text-center p-6">
              <p className="text-[#1D2939] font-medium">This job description document is unavailable.</p>
            </div>
          ) : isDocx ? (
            <div className={styles.docxScroll}>
              {docxStatus === 'loading' && (
                <p className="text-[#667085] text-center py-10">Loading document…</p>
              )}
              {docxStatus === 'error' && (
                <p className="text-[#667085] text-center py-10">
                  Couldn't preview this document here. Use the link below to open it.
                </p>
              )}
              <div ref={docxContainerRef} className={styles.docxContainer} />
            </div>
          ) : (
            <div className={styles.pdfContainer}>
              <iframe
                src={iframeSrc}
                className={styles.pdfIframe}
                title="Job Description Document"
              />
            </div>
          )}
        </div>
        {/* Always offer a direct link so the candidate is never blocked even if
            the inline preview fails to render the document. */}
        {fileUrl && (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center -mt-3 py-3 bg-[#012169] text-white font-medium hover:bg-[#003087] transition-colors"
          >
            Open in a new tab / download
          </a>
        )}
      </div>
    </div>
  );
};

export { PdfViewerModal };
